import React, { useEffect, useState } from 'react';
import {
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Box,
} from '@mui/material';

const AutoCompleteComponent = ({
  name,
  question,
  value,
  onChange,
  onNext,
  setValidationState,
  registerValidation,
  inputRef,
  nextFieldRef,
  isMobile,
  layoutVariant = 'default',
}) => {
  const isAccountLayout = layoutVariant === 'account';
  const [inputValue, setInputValue] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [error, setError] = useState('');
  const [focused, setFocused] = useState(false);

  const options = question.options || [];

  useEffect(() => {
    if (registerValidation) {
      registerValidation(name, triggerValidation);
    }
  }, [inputValue]);

  useEffect(() => {
    if (value && value !== inputValue) {
      setInputValue(value);
    }
  }, [value]);

  const validate = (input = inputValue) => {
    const isValid = !!input;
    if (!isValid && question.validation?.required) {
      setError(question.validation.message || 'Required');
    } else {
      setError('');
    }
    setValidationState?.(name, isValid);
    return isValid;
  };

  const triggerValidation = () => {
    return validate();
  };

  const handleInput = (e) => {
    const input = e.target.value;
    setInputValue(input);
    onChange?.(name, input);
    validate(input);

    if (input.length === 0) {
      setSuggestions([]);
      return;
    }

    const filtered = options.filter((opt) =>
      opt.toLowerCase().includes(input.toLowerCase())
    );
    setSuggestions(filtered);
  };

  const handleSelect = (val) => {
    setInputValue(val);
    onChange?.(name, val);
    setSuggestions([]);
    validate(val);
    onNext?.();
  };

  const sizeOverrides = {
    'extra-small': '219px',
    small: '301px',
    medium: '403px',
    large: '534px',
    half: '500px',
    full: '1000px',
    'vehicle-return': '324px',
    'vehicle-return-full': '662px',
    'vehicle-full': '448px',
    'accounts-half': '416px',
    'accounts-full': '864px',
  };

  const fieldWidth = question.customSize
    ? sizeOverrides[question.customSize] || '100%'
    : '100%';
  const responsiveWidth = isAccountLayout
    ? '100%'
    : isMobile
      ? '80vw'
      : fieldWidth;
  const menuWidth = isAccountLayout
    ? fieldWidth
    : isMobile
      ? '80vw'
      : fieldWidth;

  const fieldHeight = isMobile ? '51px' : '53px';
  const fontSize = '1rem';

  return (
    <Box
      sx={{
        width: responsiveWidth,
        maxWidth: '100%',
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: isAccountLayout ? '100%' : isMobile ? '80vw' : fieldWidth,
          maxWidth: isAccountLayout ? '100%' : fieldWidth,
          mx: isAccountLayout ? 'auto' : isMobile ? 'auto' : undefined,
        }}
      >
        <TextField
          fullWidth
          placeholder={question.placeholder}
          value={inputValue}
          onChange={handleInput}
          onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 100)}
          variant="outlined"
          error={!!error}
          helperText={error}
          inputRef={inputRef}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              const isValid = validate();
              if (isValid) {
                if (nextFieldRef?.current) {
                  nextFieldRef.current.focus();
                } else {
                  onNext?.();
                }
              }
            }
          }}
          sx={(theme) => ({
            width: isAccountLayout ? '100%' : isMobile ? '80vw' : fieldWidth,
            mx: isAccountLayout ? 'auto' : isMobile ? 'auto' : undefined,
            height: fieldHeight,
            '& .MuiOutlinedInput-root': {
              textAlign: 'left',
              borderRadius: '12px',
              height: 'auto',
              paddingTop: '16px',
              paddingRight: '12px',
              paddingBottom: '16px',
              paddingLeft: '12px',
              gap: '8px',
              borderWidth: '1px',
              '& input': {
                fontFamily: 'Inter, sans-serif',
                fontSize: fontSize,
                lineHeight: isMobile ? '100%' : '1.2rem',
                letterSpacing: '-0.02em',
                color: '#757575',
                padding: 0,
                '&::placeholder': {
                  color: '#757575',
                  opacity: 1,
                  fontFamily: 'Inter, sans-serif',
                  fontSize: fontSize,
                  lineHeight: '1.2rem',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  textAlign: 'left',
                },
              },
              '& fieldset': {
                borderRadius: '12px',
                borderWidth: '1px',
              },
            },
            '& .MuiFormHelperText-root': {
              textAlign: 'right',
              fontSize: isMobile ? '0.5rem' : '0.75rem',
              pr: 1,
              mt: '4px',
              fontFamily: 'Inter, sans-serif',
              color: theme.palette.component.error,
            },
          })}
        />

        {focused && suggestions.length > 0 && (
          <Paper
            elevation={3}
            sx={(theme) => ({
              position: 'absolute',
              top: fieldHeight,
              left: 0,
              width: menuWidth,
              minWidth: menuWidth,
              mx: isAccountLayout ? 'auto' : isMobile ? 'auto' : undefined,
              borderRadius: '0 0 20px 20px',
              border: `1px solid ${theme.palette.component.border}`,
              borderTop: 'none',
              boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
              overflowY: 'auto',
              maxHeight: '250px',
              zIndex: 10,
              '&::-webkit-scrollbar': { display: 'none' },
              msOverflowStyle: 'none',
              scrollbarWidth: 'none',
            })}
          >
            <List>
              {suggestions.map((opt, index) => (
                <ListItem key={index} disablePadding>
                  <ListItemButton onClick={() => handleSelect(opt)}>
                    <ListItemText
                      primary={opt}
                      primaryTypographyProps={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '0.875rem',
                        textAlign: 'left',
                        color: (theme) => theme.palette.component.text,
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Paper>
        )}
      </Box>
    </Box>
  );
};

export default AutoCompleteComponent;
